import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

import { BaseButton, type BaseButtonProps } from "./BaseButton";
import {
  BUTTON_BASE_CLASSES,
  BUTTON_CAUTION_CLASSES,
  BUTTON_DISABLED_CLASSES,
  BUTTON_GHOST_CLASSES,
  BUTTON_SECONDARY_CLASSES,
  BUTTON_SQUIRCLE_CLASSES,
  BUTTON_TERTIARY_CLASSES,
} from "./helpers";

import { BaseSizeType } from "mds/global-types";
import type { PolymorphicRef } from "types";

export const SQUARE_ICON_BUTTON_KINDS = [
  "secondary",
  "tertiary",
  "ghost", // light mode only
  "caution", // light mode only
  "destructive", // dark mode only
  "destructive-toggle", // dark mode only
] as const;

export type SquareIconButtonKind = (typeof SQUARE_ICON_BUTTON_KINDS)[number];

const COMMON_CLASSES = twMerge(
  BUTTON_BASE_CLASSES,
  BUTTON_DISABLED_CLASSES,
  BUTTON_SQUIRCLE_CLASSES,
  "border",
);

const KIND_CLASSES: Record<SquareIconButtonKind, string> = {
  secondary: BUTTON_SECONDARY_CLASSES,

  tertiary: BUTTON_TERTIARY_CLASSES,

  ghost: BUTTON_GHOST_CLASSES,

  caution: BUTTON_CAUTION_CLASSES,

  destructive:
    "text-white bg-red-shade-20 border-red-shade-20 " +
    "hover:bg-red-shade-40 hover:border-red-shade-40",

  "destructive-toggle":
    "text-white bg-black-tint-20 border-black-tint-20 " +
    "hover:bg-black-tint-30 hover:border-black-tint-30 " +
    "active:bg-red active:border-red " +
    "enabled:data-[selected]:bg-red enabled:data-[selected]:border-red " +
    "enabled:data-[selected]:hover:bg-red-tint-20 enabled:data-[selected]:hover:border-red-tint-20",
};

export type SquareIconButtonSize = Extract<BaseSizeType, "s" | "m" | "l">;

const SIZE_CLASSES: Record<SquareIconButtonSize, string> = {
  s: "size-[34px] min-w-[34px] min-h-[34px] *:size-[16px] rounded-[6px] supports-[(corner-shape:squircle)]:rounded-[12px] ",
  m: "size-[46px] min-w-[46px] min-h-[46px] *:size-[18px] rounded-[8px] supports-[(corner-shape:squircle)]:rounded-[16px] ",
  l: "size-[54px] min-w-[54px] min-h-[54px] *:size-[22px] rounded-[10px] supports-[(corner-shape:squircle)]:rounded-[20px] ",
};

export type SquareIconButtonProps<T extends React.ElementType> = BaseButtonProps<T> & {
  isSelected?: boolean;
  kind: SquareIconButtonKind;
  size: SquareIconButtonSize;
};

export const SquareIconButton = forwardRef<
  HTMLButtonElement,
  Omit<SquareIconButtonProps<"button">, "ref">
>(function SquareIconButton(
  { isSelected, kind, size, className, ...rest }: SquareIconButtonProps<"button">,
  ref,
) {
  return (
    <BaseButton
      className={twMerge(
        COMMON_CLASSES,
        kind ? KIND_CLASSES[kind] : "",
        size ? SIZE_CLASSES[size] : "",
        className,
      )}
      data-selected={isSelected || undefined}
      ref={ref}
      {...rest}
    />
  );
}) as <T extends React.ElementType = "button">(
  props: SquareIconButtonProps<T> & { ref?: PolymorphicRef<T> },
) => React.ReactNode;
